import { useState } from 'react';

import type { TView } from '@shared/types/view';
import type { Updater, VisibilityState } from '@tanstack/react-table';
import { removeNullishFromObj } from '@src/shared/utils/helpers/remove-nullish-from-obj';

const resolveVisibilityFallback = (
  view?: TView,
  initialVisibility: VisibilityState = {}
): VisibilityState => {
  const viewVisibility = view?.visible_static_fields?.visibility || {};
  return Object.keys(viewVisibility).length ? viewVisibility : initialVisibility;
};

export const useColumnVisibility = (
  view?: TView,
  initialVisibility: VisibilityState = {}
) => {
  // Keep empty until the user/view-switch writes state so async view load can win.
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const fallbackVisibility = resolveVisibilityFallback(view, initialVisibility);

  const derivedColumnVisibility = Object.keys(columnVisibility || {}).length
    ? columnVisibility
    : fallbackVisibility;

  function onColumnVisibilityChange(updaterOrValue: Updater<VisibilityState>) {
    if (typeof updaterOrValue === 'function') {
      const col = updaterOrValue(derivedColumnVisibility);
      setColumnVisibility(col);
    } else {
      setColumnVisibility(updaterOrValue);
    }
  }

  const isColumnVisibilityChanged =
    Object.keys(removeNullishFromObj(derivedColumnVisibility, ['true']) || {})
      .length !==
    Object.keys(removeNullishFromObj(fallbackVisibility, ['true']) || {})
      .length;

  return {
    columnVisibility: removeNullishFromObj<VisibilityState>(
      derivedColumnVisibility,
      ['true']
    ),
    onColumnVisibilityChange,
    isColumnVisibilityChanged,
  };
};
